<script setup lang="ts">
import WrButton from "~/components/global/WrButton.vue";
import WrIcon from "~/components/global/WrIcon.vue";

const colorMode = useColorMode();

interface DeliverableCard {
  number: string;
  title: string;
  description: string;
  svgIcon?: string;
}

const props = withDefaults(
  defineProps<{
    heading: string;
    subcopy: string;
    cards: DeliverableCard[];
    svgIcons?: string[];
    ctaText?: string;
    ctaLink?: string | null;
  }>(),
  {
    svgIcons: () => [],
    ctaText: "Get Your Complete Logo Kit",
    ctaLink: null,
  },
);

const emit = defineEmits<{
  "cta-clicked": [];
}>();
</script>

<template>
  <div class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px] text-center">
    <h2
      class="text-[28px] lg:text-[48px] font-semibold leading-tight mx-auto px-4 font-avant text-transparent bg-[linear-gradient(92deg,#12DBFF_-3.57%,#BA55FE_80.72%)] bg-clip-text"
    >
      <span v-html="heading" />
    </h2>
    <p
      class="self-stretch text-center mt-4 px-4 text-[16px] lg:text-[18px] lg:opacity-70 max-w-[600px] mx-auto font-avant font-normal leading-[22px] tracking-[0.36px]"
      :class="colorMode.preference === 'dark' ? 'text-white' : 'text-black'"
    >
      {{ subcopy }}
    </p>

    <!-- Cards Grid -->
    <div
      class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mt-10 px-4 max-w-[1100px] mx-auto"
    >
      <div
        v-for="(card, index) in cards"
        :key="card.title"
        class="flex flex-col p-6 border rounded-xl transition-shadow duration-300 hover:shadow-lg"
        :class="
          colorMode.preference === 'dark'
            ? 'border-[#3A2A7B] bg-[#1B022B] hover:shadow-[#4902A6]/20'
            : 'border-[#E7CFFF] bg-white hover:shadow-[#A361FF]/15'
        "
      >
        <!-- Icon -->
        <div class="flex justify-center">
          <slot :name="`icon-${index}`" :index="index">
            <WrIcon
              v-if="card.svgIcon && card.svgIcon.startsWith('streamline:')"
              :name="card.svgIcon"
              class="size-6 sm:size-8 text-[#501D71] dark:text-white"
            />
          </slot>
        </div>

        <!-- Content -->
        <h3
          class="font-semibold text-[18px] leading-normal mt-4"
          :class="
            colorMode.preference === 'dark' ? 'text-white' : 'text-[#613FAA]'
          "
        >
          {{ card.title }}
        </h3>
        <p
          class="mt-2 text-[15px] leading-5"
          :class="
            colorMode.preference === 'dark' ? 'text-[#AE5DE8]' : 'text-black'
          "
        >
          {{ card.description }}
        </p>
      </div>
    </div>

    <!-- CTA Button -->
    <div class="flex justify-center mt-6 lg:mt-12">
      <WrButton
        :link="ctaLink || undefined"
        @click="!ctaLink ? emit('cta-clicked') : undefined"
        container-class="h-[40px] lg:h-[48px]"
        append-class="text-sm lg:font-normal px-3 lg:px-5"
      >
        {{ ctaText }}
      </WrButton>
    </div>
  </div>
</template>
